import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Box,
  Divider,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { SolidButton } from "components/buttons/SolidButton";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import TableField from "components/Table";
import GlobalAlert from "components/Notification";
import Loader from "components/Loader";
import LinkModal from "components/LinkModal";
import useZustandStore from "zustandStore";

import Api from "./util/api";
import { IPV4_ACL_RULE_LIMIT } from "./util/const";
import {
  cloneIpv4AclRules,
  createDefaultIpv4AclRule,
  formatEndpointAddress,
  formatPolicy,
  formatPort,
  formatProtocol,
  fromApiIpv4AclRule,
  normalizeIpv4AclRules,
  toApiIpv4AclRule,
} from "./util/func";
import { getTableColumns } from "./util/columns";
import RuleModal from "./Modal/RuleModal";
import RemoveRuleModal from "./Modal/RemoveRuleModal";

const WiredIPv4ACL = ({ showEditGeneral, onIpv4AclRuleCountChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { auth, isAuthReady } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const isUnityProSubscribed = serviceStatus?.services?.unityPro === true;
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const [uuidList] = useState({
    read: uuidv4(),
    update: uuidv4(),
  });
  const [draftRules, setDraftRules] = useState([]);
  const [originRules, setOriginRules] = useState([]);
  const [ruleModal, setRuleModal] = useState({
    open: false,
    mode: "add",
    rule: null,
  });
  const [removeRule, setRemoveRule] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const ruleLimitReached = draftRules.length >= IPV4_ACL_RULE_LIMIT;
  const handleEditRule = useCallback((rule) => {
    setRuleModal({
      open: true,
      mode: "edit",
      rule: cloneIpv4AclRules([rule])[0],
    });
  }, []);
  const handleRemoveRule = useCallback((rule) => {
    setRemoveRule(cloneIpv4AclRules([rule])[0]);
  }, []);
  const tableData = useMemo(
    () => draftRules.map((rule) => ({
      id: rule.seq,
      seq: rule.seq,
      policyLabel: formatPolicy(rule.policy),
      protocolLabel: formatProtocol(rule.protocol),
      sourceLabel: formatEndpointAddress(rule.source),
      srcPortLabel: formatPort(rule.source),
      destinationLabel: formatEndpointAddress(rule.destination),
      dstPortLabel: formatPort(rule.destination),
      comment: rule.comment,
      ref: rule,
    })),
    [draftRules]
  );
  const columnsData = useMemo(
    () => getTableColumns({
      columnHelper,
      showEditGeneral,
      disabled: isSaving,
      onEdit: handleEditRule,
      onRemove: handleRemoveRule,
    }),
    [columnHelper, handleEditRule, handleRemoveRule, isSaving, showEditGeneral]
  );
  const table = useTableInstance({
    tableName: "wiredIpv4AclTable",
    data: tableData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: false,
  });

  const fetchIpv4AclRules = () => {
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.read);

    sendRequest(apiKey, uuidList.read, params);
  };

  const updateIpv4AclRules = (payload) => {
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update);

    setIsSaving(true);
    sendRequest(
      apiKey,
      uuidList.update,
      params,
      payload,
    );
  };

  useEffect(() => {
    if (isAuthReady && serviceStatus) fetchIpv4AclRules();
  }, [isAuthReady, serviceStatus]);

  useEffect(() => {
    const readResponse = getData(uuidList.read);

    if (readResponse) {
      if (readResponse.error || (readResponse.hasOwnProperty("code") && ![0, 200].includes(readResponse.code))) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: readResponse.error?.message || readResponse.message || "Failed to read IPv4 ACL rules.",
        });
        return;
      }

      const normalizedRules = normalizeIpv4AclRules((readResponse.data || []).map(fromApiIpv4AclRule));

      setOriginRules(cloneIpv4AclRules(normalizedRules));
      setDraftRules(cloneIpv4AclRules(normalizedRules));
      onIpv4AclRuleCountChange?.(normalizedRules.length);
    }
  }, [dataMap?.[uuidList.read]]);

  useEffect(() => {
    const updateResponse = getData(uuidList.update);

    if (updateResponse) {
      if (updateResponse.error || (updateResponse.hasOwnProperty("code") && ![0, 200].includes(updateResponse.code))) {
        setIsSaving(false);
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse.error?.message || updateResponse.message || "Failed to update IPv4 ACL rules.",
        });
        return;
      }

      handleCloseRuleModal();
      setRemoveRule(null);
      setIsSaving(false);
      fetchIpv4AclRules();
    }
  }, [dataMap?.[uuidList.update]]);

  const handleOpenAddModal = () => {
    setRuleModal({
      open: true,
      mode: "add",
      rule: createDefaultIpv4AclRule(),
    });
  };

  const handleCloseRuleModal = () => {
    setRuleModal({
      open: false,
      mode: "add",
      rule: null,
    });
  };

  const handleApplyRule = (rule) => {
    if (!isUnityProSubscribed) {
      setIsLinkModalOpen(true);
      return;
    }

    const isEditMode = ruleModal.mode === "edit";
    const payload = isEditMode
      ? { update: [toApiIpv4AclRule(rule, true)] }
      : { create: [toApiIpv4AclRule(rule)] };

    updateIpv4AclRules(payload);
  };

  const handleCloseRemoveModal = () => {
    setRemoveRule(null);
  };

  const handleConfirmRemove = () => {
    if (!removeRule) return;

    const payload = {
      delete: { ids: [removeRule.id] },
    };

    updateIpv4AclRules(payload);
  };

  if (!serviceStatus) {
    return <Loader />;
  }

  return (
    <Box sx={{ padding: "20px 30px" }}>
      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontSize: theme.typography.customFontSize[5],
          fontWeight: theme.typography.customFontWeight[1],
        }}
      >
        Global Settings
      </Typography>
      <Divider sx={{ margin: "20px 0" }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>
          Rule Quota: {draftRules.length} / {IPV4_ACL_RULE_LIMIT}
        </Typography>

        {showEditGeneral && (
          <SolidButton
            sx={{ whiteSpace: "nowrap" }}
            iconStart={<AddIcon />}
            width="auto"
            height="35px"
            disabled={ruleLimitReached || isSaving}
            onClick={handleOpenAddModal}
          >
            Add User Defined IPv4 ACL Rule
          </SolidButton>
        )}
      </Box>

      <TableField
        {...table}
        pendingChildren="No entry in the table"
      />

      <RuleModal
        open={ruleModal.open}
        mode={ruleModal.mode}
        rule={ruleModal.rule}
        isSaving={isSaving}
        showEditGeneral={showEditGeneral}
        onClose={handleCloseRuleModal}
        onApply={handleApplyRule}
      />
      <RemoveRuleModal
        open={Boolean(removeRule)}
        rule={removeRule}
        isSaving={isSaving}
        onCancel={handleCloseRemoveModal}
        onConfirm={handleConfirmRemove}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />

      <LinkModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        transparentBackdrop
        content="IPv4 ACL is a Nuclias Unity Pro feature. Please subscribe to Nuclias Unity Pro to save these changes."
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </Box>
  );
};

export default WiredIPv4ACL;
